import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowBack as ArrowBackIcon, ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { vouchersApi, formatAmount, formatDate } from '../../services/expenseVouchersApi';
import { useExpenseBase, VoucherStatusChip } from '../../components/expense/voucherShared';
import ExpenseLineEditor from '../../components/expense/ExpenseLineEditor';
import useVoucherLookups from '../../components/expense/useVoucherLookups';

let keySeq = 0;
const nextKey = () => { keySeq += 1; return `line-${keySeq}`; };

const BILL_OPTIONAL = ['ADVANCED', 'NEW_ADVANCED'];

const REQUIRED = [
  ['expenseDate', 'Expense date is required'],
  ['vendorKind', 'Vendor is required'],
  ['categoryId', 'Expense category is required'],
  ['headId', 'Expense head is required'],
  ['amount', 'Amount is required'],
  ['currency', 'Currency is required'],
  ['employmentCountryId', 'Employment country is required'],
  ['expenseCountryId', 'Expense country is required'],
  ['paymentType', 'Payment type is required'],
  ['description', 'Description is required'],
];

const emptyLine = (defaults = {}) => ({
  key: nextKey(),
  _id: undefined,
  expenseDate: '',
  vendorKind: 'SELF',
  vendorId: '',
  vendorUserId: '',
  vendorName: '',
  categoryId: '',
  headId: '',
  employmentCountryId: '',
  expenseCountryId: '',
  currency: '',
  amount: '',
  paymentType: '',
  paidViaId: '',
  description: '',
  bills: [],
  ...defaults,
});

const fromServerLine = (l, key) => ({
  key: key || nextKey(),
  _id: l._id,
  expenseDate: l.expenseDate ? String(l.expenseDate).slice(0, 10) : '',
  vendorKind: l.vendor?.kind || '',
  vendorId: l.vendor?.vendorId || '',
  vendorUserId: l.vendor?.userId || '',
  vendorName: l.vendor?.name || '',
  categoryId: l.categoryId || '',
  categoryName: l.categoryName,
  headId: l.headId || '',
  headName: l.headName,
  employmentCountryId: l.employmentCountryId || '',
  employmentCountryName: l.employmentCountryName,
  expenseCountryId: l.expenseCountryId || '',
  expenseCountryName: l.expenseCountryName,
  currency: l.currency || '',
  amount: typeof l.amount === 'number' ? String(l.amount) : '',
  paymentType: l.paymentType || '',
  paidViaId: l.paidViaId || '',
  paidViaName: l.paidViaName,
  description: l.description || '',
  bills: l.bills || [],
});

// Only fields the API accepts; totals / owner / status are never sent.
const toPayloadLine = (l) => {
  const amount = String(l.amount).trim();
  return {
    ...(l._id ? { _id: l._id } : {}),
    expenseDate: l.expenseDate || null,
    vendorKind: l.vendorKind || null,
    vendorId: l.vendorKind === 'VENDOR' ? l.vendorId || null : null,
    vendorUserId: l.vendorKind === 'EMPLOYEE' ? l.vendorUserId || null : null,
    categoryId: l.categoryId || null,
    headId: l.headId || null,
    employmentCountryId: l.employmentCountryId || null,
    expenseCountryId: l.expenseCountryId || null,
    currency: l.currency || null,
    amount: amount === '' ? null : (Number.isFinite(Number(amount)) ? Number(amount) : amount),
    paymentType: l.paymentType || null,
    paidViaId: l.paymentType === 'PAID_VIA' ? l.paidViaId || null : null,
    description: l.description || null,
  };
};

// Mirrors the server's submit checks so errors show before a round trip.
export const validateForSubmit = (lines) => {
  const errors = {};
  if (!lines.length) errors.lines = 'Add at least one expense line before submitting';
  lines.forEach((l, i) => {
    REQUIRED.forEach(([f, msg]) => { if (!String(l[f] ?? '').trim()) errors[`lines.${i}.${f}`] = msg; });
    if (l.vendorKind === 'VENDOR' && !l.vendorId) errors[`lines.${i}.vendorId`] = 'Select a vendor';
    if (l.vendorKind === 'EMPLOYEE' && !l.vendorUserId) errors[`lines.${i}.vendorUserId`] = 'Select an employee';
    if (String(l.amount).trim() && !(Number(l.amount) >= 1)) errors[`lines.${i}.amount`] = 'Amount must be at least 1';
    if (l.paymentType === 'PAID_VIA' && !l.paidViaId) errors[`lines.${i}.paymentType`] = 'Select which card / account paid';
    if (l.paymentType && !BILL_OPTIONAL.includes(l.paymentType) && !l.bills.length) {
      errors[`lines.${i}.bills`] = 'Upload at least one bill for this expense';
    }
  });
  return errors;
};

// erp_expense preselects "Advanced" on a normal voucher (unless an advance is unsettled,
// when "Advanced" is not offered) and "Settlement" on a settlement voucher.
const defaultPaymentType = (linkedVoucher, hasUnsettled) => {
  if (linkedVoucher) return 'SETTLEMENT';
  return hasUnsettled ? '' : 'ADVANCED';
};

const lineErrors = (errors, index) => {
  const prefix = `lines.${index}.`;
  return Object.fromEntries(Object.entries(errors)
    .filter(([k]) => k.startsWith(prefix))
    .map(([k, v]) => [k.slice(prefix.length), v]));
};

// Create / edit an employee Expense Voucher (DRAFT or SENT_BACK only).
const ExpenseVoucherForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useExpenseBase();
  const { user } = useAuth();
  const lookups = useVoucherLookups();
  const { loadHeads } = lookups;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [context, setContext] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [lines, setLines] = useState([]);
  const [errors, setErrors] = useState({});
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [conflict, setConflict] = useState(false);
  const loadedId = useRef(null);

  const applyVoucher = useCallback((v) => {
    setVoucher(v);
    setInvoiceNumber(v.invoiceNumber || '');
    setLines((prev) => v.lines.map((l, i) => fromServerLine(l, prev[i]?.key)));
    setDirty(false);
    loadedId.current = v._id;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setConflict(false);
    try {
      const [ctx, res] = await Promise.all([
        vouchersApi.formContext(id),
        id ? vouchersApi.get(id) : Promise.resolve(null),
      ]);
      setContext(ctx.data);
      if (res) {
        if (!res.data.canEdit) {
          navigate(`${base}/${id}`, { replace: true });
          return;
        }
        applyVoucher(res.data);
      } else {
        const defaultCountry = ctx.data.defaultEmploymentCountryId || '';
        setLines([emptyLine({
          employmentCountryId: defaultCountry,
          paymentType: defaultPaymentType(null, ctx.data.hasUnsettledAdvance),
        })]);
      }
      setErrors({});
      setLoadError(null);
    } catch (e) {
      setLoadError(e);
    } finally {
      setLoading(false);
    }
  }, [id, base, navigate, applyVoucher]);

  // After the first save the route changes /new -> /:id/edit; don't reload what we just saved
  useEffect(() => {
    if (id && loadedId.current === id) return;
    load();
  }, [id, load]);

  // New-line default currency follows the default employment country once masters load
  useEffect(() => {
    if (lookups.loading) return;
    setLines((prev) => prev.map((l) => {
      if (l._id || l.currency || !l.employmentCountryId) return l;
      const cur = lookups.countries.find((c) => c._id === l.employmentCountryId)?.defaultCurrency;
      return cur ? { ...l, currency: cur } : l;
    }));
  }, [lookups.loading, lookups.countries]);

  const categoryKey = lines.map((l) => l.categoryId).join(',');
  useEffect(() => {
    categoryKey.split(',').filter(Boolean).forEach(loadHeads);
  }, [categoryKey, loadHeads]);

  const updateLine = (index, patch) => {
    setLines((prev) => prev.map((l, i) => (i === index ? { ...l, ...patch } : l)));
    setDirty(true);
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(patch).forEach((f) => {
        delete next[`lines.${index}.${f}`];
        if (f.startsWith('vendor')) ['vendorId', 'vendorUserId', 'vendorKind', 'vendor'].forEach((k) => delete next[`lines.${index}.${k}`]);
      });
      return next;
    });
  };

  const addLine = () => {
    const last = lines[lines.length - 1];
    setLines((prev) => [...prev, emptyLine({
      employmentCountryId: last?.employmentCountryId || context?.defaultEmploymentCountryId || '',
      currency: last?.currency || '',
      paymentType: defaultPaymentType(voucher?.linkedVoucher, context?.hasUnsettledAdvance),
    })]);
    setDirty(true);
  };

  const removeLine = (index) => {
    const line = lines[index];
    if (line.bills.length && !window.confirm('Remove this line? Its bills will be deleted when you save.')) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
    setErrors({});
    setDirty(true);
  };

  const hasUnsettled = context?.hasUnsettledAdvance;
  // erp_expense: "Advanced" for a first advance, "New Advanced" only with an unsettled
  // advance, "Settlement" only on settlement vouchers. The server re-checks.
  const paymentTypeOptions = useCallback((current) => (context?.paymentTypes || []).filter((p) => {
    if (p.value === current) return true;
    if (p.value === 'SETTLEMENT') return Boolean(voucher?.linkedVoucher);
    if (voucher?.linkedVoucher) return false;
    if (p.value === 'ADVANCED') return !hasUnsettled;
    if (p.value === 'NEW_ADVANCED') return hasUnsettled;
    return true;
  }), [context, voucher, hasUnsettled]);

  const handleApiError = (e) => {
    if (e.code === 'VERSION_CONFLICT') setConflict(true);
    if (e.errors && Object.keys(e.errors).length) setErrors(e.errors);
    toast.error(e.message);
  };

  // Saves the draft; returns the saved voucher (or null on failure)
  const save = async ({ quiet = false } = {}) => {
    const body = { invoiceNumber, lines: lines.map(toPayloadLine) };
    try {
      const res = voucher
        ? await vouchersApi.update(voucher._id, { ...body, version: voucher.version })
        : await vouchersApi.create(body);
      applyVoucher(res.data);
      setErrors({});
      if (!quiet) toast.success(res.message || 'Draft saved');
      if (!voucher) navigate(`${base}/${res.data._id}/edit`, { replace: true });
      return res.data;
    } catch (e) {
      handleApiError(e);
      return null;
    }
  };

  const handleSave = async () => {
    setBusy(true);
    await save();
    setBusy(false);
  };

  const handleSubmit = async () => {
    const clientErrors = validateForSubmit(lines);
    if (Object.keys(clientErrors).length) {
      setErrors(clientErrors);
      toast.error(clientErrors.lines || 'Please fix the highlighted fields before submitting');
      return;
    }
    if (!window.confirm('Apply for approval? The voucher will be sent to Accounts and you will not be able to edit it afterwards.')) return;

    setBusy(true);
    try {
      const saved = !voucher || dirty ? await save({ quiet: true }) : voucher;
      if (!saved) return;
      const res = await vouchersApi.submit(saved._id, saved.version);
      toast.success(res.message || 'Voucher submitted');
      navigate(`${base}/${saved._id}`);
    } catch (e) {
      handleApiError(e);
    } finally {
      setBusy(false);
    }
  };

  // Bills need a saved line: save first when the line is new or the form has changes.
  const mergeBills = (v) => {
    setVoucher(v);
    setLines((prev) => prev.map((l) => ({ ...l, bills: v.lines.find((x) => x._id === l._id)?.bills ?? l.bills })));
  };

  const uploadBills = async (index, files) => {
    try {
      let current = voucher;
      let lineId = lines[index]._id;
      if (!current || dirty || !lineId) {
        current = await save({ quiet: true });
        if (!current) return;
        lineId = current.lines[index]?._id;
      }
      const res = await vouchersApi.uploadBills(current._id, lineId, files);
      mergeBills(res.data);
      setErrors((prev) => { const n = { ...prev }; delete n[`lines.${index}.bills`]; return n; });
      toast.success(files.length > 1 ? `${files.length} bills uploaded` : 'Bill uploaded');
    } catch (e) {
      if (e.code === 'VERSION_CONFLICT') setConflict(true);
      toast.error(`Upload failed: ${e.message}`);
    }
  };

  const deleteBill = async (bill) => {
    if (!window.confirm(`Delete bill "${bill.originalName}"?`)) return;
    try {
      const res = await vouchersApi.deleteBill(voucher._id, bill._id);
      mergeBills(res.data);
      toast.success('Bill deleted');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const totals = useMemo(() => {
    const map = new Map();
    lines.forEach((l) => {
      const n = Number(l.amount);
      if (!String(l.amount).trim() || !Number.isFinite(n)) return;
      map.set(l.currency || '', (map.get(l.currency || '') || 0) + n);
    });
    return [...map.entries()];
  }, [lines]);

  const goBack = () => {
    if (dirty && !window.confirm('Discard unsaved changes?')) return;
    navigate(base);
  };

  if (loading) {
    return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity={loadError.status === 403 || loadError.status === 404 ? 'warning' : 'error'}>
          {loadError.status === 404 ? 'Voucher not found.' : loadError.message}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(base)} sx={{ mt: 2, textTransform: 'none' }}>Back to My Expenses</Button>
      </Box>
    );
  }

  const selfName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Self';
  const missingMasters = [
    !lookups.loading && !lookups.categories.length && 'expense categories',
    !lookups.loading && !lookups.operatingCountries.length && 'operating countries',
    !lookups.loading && !lookups.currencies.length && 'currencies',
  ].filter(Boolean);
  const lastSentBack = voucher?.status === 'SENT_BACK'
    ? [...(voucher.history || [])].reverse().find((h) => h.comment && h.toStatus === 'SENT_BACK')
    : null;

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiptLongIcon color="primary" />
            {voucher ? `Edit Expense ${voucher.voucherNumber}` : 'Create Expense'}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Save as draft at any time. All fields and bills are checked when you apply for approval.
          </Typography>
        </Box>
        <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ textTransform: 'none' }}>My Expenses</Button>
      </Box>

      {conflict && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => { loadedId.current = null; load(); }}>Reload</Button>}>
          This voucher was changed in another tab or window. Reload to get the latest version (your unsaved changes here will be lost).
        </Alert>
      )}
      {voucher?.status === 'SENT_BACK' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This voucher was sent back for changes.{lastSentBack ? ` Comment: ${lastSentBack.comment}` : ''} Update it and submit again.
        </Alert>
      )}
      {context?.approvers?.message && (
        <Alert severity="info" sx={{ mb: 2 }}>{context.approvers.message} You can still save drafts.</Alert>
      )}
      {missingMasters.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No {missingMasters.join(', ')} are set up yet, so expense lines cannot be completed. Please contact Accounts.
        </Alert>
      )}
      {errors.lines && <Alert severity="error" sx={{ mb: 2 }}>{errors.lines}</Alert>}
      {errors.approvers && <Alert severity="error" sx={{ mb: 2 }}>{errors.approvers}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Voucher information</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Expense Type" value="Employee Expense" disabled />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Voucher Number" value={voucher?.voucherNumber || 'Assigned on first save'} disabled />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField size="small" fullWidth label="Voucher Date" value={formatDate(voucher?.voucherDate || new Date())} disabled />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              size="small"
              fullWidth
              label="Invoice Number"
              value={invoiceNumber}
              inputProps={{ maxLength: 100 }}
              error={Boolean(errors.invoiceNumber)}
              helperText={errors.invoiceNumber}
              onChange={(e) => { setInvoiceNumber(e.target.value); setDirty(true); }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">Status:</Typography>
              <VoucherStatusChip status={voucher?.status || 'DRAFT'} />
              {dirty && <Typography variant="caption" color="warning.main">Unsaved changes</Typography>}
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="h6" sx={{ mb: 1 }}>Expense lines</Typography>
      {lines.map((line, index) => (
        <ExpenseLineEditor
          key={line.key}
          index={index}
          line={line}
          errors={lineErrors(errors, index)}
          lookups={{ ...lookups, selfName }}
          paymentTypeOptions={paymentTypeOptions}
          allowPaidVia={!voucher?.linkedVoucher}
          onChange={(patch) => updateLine(index, patch)}
          onRemove={() => removeLine(index)}
          canRemove
          voucherId={voucher?._id}
          billRules={context?.billRules}
          onUploadBills={(files) => uploadBills(index, files)}
          onDeleteBill={deleteBill}
          disabled={busy}
        />
      ))}
      {!lines.length && (
        <Paper variant="outlined" sx={{ p: 3, mb: 2, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No expense lines yet.</Typography>
        </Paper>
      )}
      <Button startIcon={<AddIcon />} onClick={addLine} disabled={busy} sx={{ textTransform: 'none', mb: 3 }}>
        Add expense line
      </Button>

      <Paper sx={{ p: 2, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">Total (recalculated by the server on save)</Typography>
          <Typography variant="h6" data-testid="voucher-total">
            {totals.length ? totals.map(([cur, amt]) => formatAmount(Math.round(amt * 10000) / 10000, cur)).join(' + ') : '0'}
          </Typography>
        </Box>
        <Button variant="outlined" onClick={handleSave} disabled={busy} sx={{ textTransform: 'none' }}>
          {busy ? 'Saving...' : 'Save Draft'}
        </Button>
        {/* erp_expense: "Apply For Approval" is disabled until the voucher has a line */}
        <Button variant="contained" onClick={handleSubmit} disabled={busy || !lines.length} sx={{ textTransform: 'none' }}>
          Apply For Approval
        </Button>
      </Paper>
    </Box>
  );
};

export default ExpenseVoucherForm;
